import firebase from "firebase"

import { db } from "../firebase"
import { BOARD_TYPES } from "../exports/board-types"
import { buildItineraryNodes, attachItemDiscountsToNodes, discountChosenLabel } from "../../containers/enquiry/_components/new-quote/itinerary-timeline"

/**
 * Publishing pipeline for the client-facing online quote.
 *
 * A quote doc (enquiries/{id}/quotes/{quoteID}) holds INTERNAL data - cost
 * prices, margin, sale profit, commission, exchange rates, raw package price
 * windows. Booking docs are world-readable, and so is the public quote, so we
 * must never expose any of that. `buildPublicQuoteDoc` produces a sanitised,
 * client-safe projection containing only what the public page renders, and
 * `publishPublicQuote` writes it to a token-gated `public_quotes/{token}` doc.
 *
 * The deposit shown on the page is computed from these safe fields, and the
 * deposit actually charged is recomputed server-side from the real quote - the
 * published doc is display-only and never the source of truth for money.
 */

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100

/**
 * Sum, in GBP, the cost of every flight the agent flagged "add to deposit"
 * (chargeFullFlight). Mirrors the builder's flight costing: a per-person price is
 * x group size, a total price is taken as-is, then converted to GBP via the
 * manual exchange rate (resort-units per £1). Used to top up the deposit so we
 * can ticket flights immediately when a quote is booked. Server-mirrored in
 * functions-payments/quote-deposit.ts - keep the two in step.
 */
export const computeFlightDeposit = (basket, exchangeRates = {}, groupSize = 0) => {
	const size = Math.max(0, Math.floor(Number(groupSize) || 0))
	const total = (Array.isArray(basket) ? basket : [])
		.filter((item) => item.type === "flight" && item.chargeFullFlight)
		.reduce((sum, flight) => {
			const price = Number(flight.price) || 0
			const native = flight.priceMode === "total" ? price : price * size
			const currency = (flight.currency || "gbp").toLowerCase()
			if (currency === "gbp") return sum + native
			const rate = parseFloat(exchangeRates[currency]) || 0
			return rate > 0 ? sum + native / rate : sum
		}, 0)
	return round2(total)
}

/**
 * Origin for client-facing quote links. Empty by default, which falls back to
 * the current window origin - i.e. the staff app domain (ghdsystem.com) the
 * agent is on when they copy a link or send a quote, matching the original
 * behaviour. Once the dedicated quotes subdomain is connected, pin links to it
 * by setting REACT_APP_QUOTE_BASE_URL (or this default) to
 * "https://quotes.golfholidaysdirect.com".
 */
const QUOTE_BASE_URL = (process.env.REACT_APP_QUOTE_BASE_URL ?? "").replace(/\/+$/, "")

const quoteOrigin = () => {
	if (QUOTE_BASE_URL) return QUOTE_BASE_URL
	return typeof window !== "undefined" && window.location ? window.location.origin : ""
}

// Deep-strip `undefined` (Firestore rejects it). Itinerary nodes carry optional
// fields (carrier, board, ...) that can be undefined; everything else is plain
// JSON-safe data, so a recursive strip is enough.
const stripUndefined = (value) => {
	if (Array.isArray(value)) return value.map(stripUndefined)
	if (value && typeof value === "object") {
		const out = {}
		Object.keys(value).forEach((key) => {
			if (value[key] !== undefined) out[key] = stripUndefined(value[key])
		})
		return out
	}
	return value
}

/**
 * Resolve the client-facing sell figures from a quote. Total = the split tiers
 * (price x qty) when split pricing is on, else the single per-person price x
 * group size. Per-person is the blended figure. No cost data is read.
 */
export const resolveQuoteSell = (quote) => {
	const groupSize = Math.max(0, Math.floor(Number(quote?.group_size) || 0))
	const splitSell = Boolean(quote?.sell_split)

	const sellLines = splitSell && Array.isArray(quote?.sell_lines)
		? quote.sell_lines
				.map((line) => ({ price: round2(line.price), qty: Math.max(0, Math.floor(Number(line.qty) || 0)) }))
				.filter((line) => line.price > 0 || line.qty > 0)
		: []

	// Total is summed from the RAW line prices then rounded once, byte-identical to
	// the server's resolveQuoteSellTotal (rounding each line first could drift the
	// displayed total a penny from the amount actually charged). The rounded
	// `sellLines` above are for per-tier display only.
	const sellTotal = splitSell
		? round2((quote?.sell_lines || []).reduce((sum, line) => sum + (Number(line.price) || 0) * Math.max(0, Math.floor(Number(line.qty) || 0)), 0))
		: round2((Number(quote?.sell_at) || 0) * groupSize)

	const sellPerPerson = groupSize > 0 ? round2(sellTotal / groupSize) : round2(Number(quote?.sell_at) || 0)

	return { groupSize, splitSell, sellLines, sellTotal, sellPerPerson }
}

const TRANSFER_LABELS = {
	airport: "Airport transfers",
	golf: "Golf transfers",
	airport_golf: "Airport & golf transfers",
	resort_shuttle: "Resort golf shuttles",
}

const flightLabel = (flight) => {
	const legs = flight.legs || []
	const isReturn = flight.tripType === "return" && legs.length > 1
	const carrier = (legs.find((leg) => leg.carrier) || {}).carrier || ""
	return `${isReturn ? "Return flights" : "Flights"}${carrier ? ` with ${carrier}` : ""}`
}

/**
 * Client-safe "what's included" summary lines, mirroring the enquiry draft-quote
 * cards: golf rounds (grouped with a quantity), flights and transfers. No board
 * / room detail (that's in the itinerary) and never any cost data.
 *
 * Exported so the quote email can render the same stripped-back summary without
 * re-deriving the rules.
 */
export const buildSummaryLines = (basket) => {
	const lines = []

	// Board type(s) across the stay(s), shown once even when several hotels share
	// a board. Board also appears per-hotel on the itinerary timeline; this is the
	// at-a-glance "what's included" echo the client first scans.
	const boards = [
		...new Set(
			basket
				.filter((item) => item.type === "hotel" && item.selectedBoard)
				.map((item) => BOARD_TYPES[item.selectedBoard] || item.selectedBoard)
				.filter(Boolean),
		),
	]
	boards.forEach((board) => lines.push({ kind: "board", text: board }))

	const rounds = basket.filter((item) => item.type === "round")
	const courseCounts = rounds.reduce((list, item) => {
		if (!item.title) return list
		const existing = list.find((entry) => entry.name === item.title)
		if (existing) existing.qty += 1
		else list.push({ name: item.title, qty: 1 })
		return list
	}, [])
	courseCounts.forEach((course) => lines.push({ kind: "round", text: `${course.qty} x ${course.name}` }))

	// Flights / transfers de-duplicated by their display label, so e.g. three
	// golf transfers read as a single "Golf transfers" line rather than three.
	const flightLabels = [...new Set(basket.filter((item) => item.type === "flight").map((flight) => flightLabel(flight)))]
	flightLabels.forEach((text) => lines.push({ kind: "flight", text }))

	const transferLabels = [...new Set(basket.filter((item) => item.type === "transfer").map((transfer) => TRANSFER_LABELS[transfer.transferType] || "Transfers"))]
	transferLabels.forEach((text) => lines.push({ kind: "transfer", text }))

	// Euro Tunnel crossings read as one line however many are on the quote.
	if (basket.some((item) => item.type === "eurotunnel")) lines.push({ kind: "transfer", text: "Euro Tunnel crossing" })

	return lines
}

/**
 * The card's lead image: the first hotel with a photo, else the first golf
 * round with one. Exported so the quote email picks the same image as the page.
 */
export const pickHeroImage = (basket) => {
	const items = Array.isArray(basket) ? basket : []
	const hotels = items.filter((item) => item.type === "hotel")
	const rounds = items.filter((item) => item.type === "round")
	return hotels.find((item) => item.image)?.image || rounds.find((item) => item.image)?.image || ""
}

/**
 * Build the sanitised public-quote payload (everything except server
 * timestamps, which the writer stamps).
 */
export const buildPublicQuoteDoc = ({ quote, token, enquiryID, quoteID, clientID }) => {
	const basket = Array.isArray(quote?.basket) ? quote.basket : []
	const hotels = basket.filter((item) => item.type === "hotel")

	// Distinct names: a hotel added twice (e.g. a package + a supplement) is one
	// stay, so it should head the quote once - matching the merged itinerary.
	const hotelNames = [...new Set(hotels.map((item) => item.title).filter(Boolean))]
	const heroImage = pickHeroImage(basket)

	const { groupSize, splitSell, sellLines, sellTotal, sellPerPerson } = resolveQuoteSell(quote)

	// Applied discounts the agent chose to show on the public page. The choice is
	// display-only (the discount is already baked into the price); a discount is
	// hidden when its "{itemUid}:{discountId}" key is in `hidden_discounts`.
	// Absent = show everything. Surfaced two ways: distinct labels in the summary
	// "Offers included" block, and a line on each item they were applied to.
	const hiddenDiscounts = new Set(Array.isArray(quote?.hidden_discounts) ? quote.hidden_discounts : [])
	const isHidden = ({ uid, id }) => hiddenDiscounts.has(`${uid}:${id}`)
	const nodes = attachItemDiscountsToNodes(buildItineraryNodes(basket), basket, isHidden)
	const offers = [
		...new Set(
			basket.flatMap((item) =>
				(Array.isArray(item.discountsChosen) ? item.discountsChosen : [])
					.filter((discount) => !isHidden({ uid: item.uid, id: discount.id }))
					.map((discount) => discountChosenLabel(discount)),
			),
		),
	]

	return stripUndefined({
		token,
		enquiry_id: enquiryID,
		quote_id: quoteID,
		// The owning client (clients/{id}). Stamped so a sent quote keeps showing on
		// the client's profile after the enquiry is deleted on conversion - the
		// client Quotes tab lists `public_quotes where client == {id}`. Left undefined
		// (and stripped) when unknown, so a re-publish never blanks a stored value.
		client: clientID || undefined,
		reference: quote?.reference || "",
		// NB: `status` is intentionally NOT set here - it's owned by the lifecycle
		// (publish sets "active" once; the deposit webhook flips it to "booked";
		// cancelling flips it to "cancelled") and must survive a re-publish.

		// Trip parameters (client-safe).
		group_size: groupSize,
		travel_millis: Number(quote?.travel_millis) || 0,
		nights: Number(quote?.nights) || 0,
		rounds: Number(quote?.rounds) || 0,

		// Itinerary - pre-built, display-only nodes (no cost data), with any shown
		// offers attached to the items they apply to.
		nodes,

		// Headline / summary.
		hotel_names: hotelNames,
		hero_image: heroImage,
		// "What's included" lines for the summary card (no cost data).
		summary_lines: buildSummaryLines(basket),
		// Distinct applied-discount labels the agent chose to advertise, shown in
		// the summary card's "Offers included" block. [] renders nothing.
		offers,

		// Client-facing pricing only.
		sell_total: sellTotal,
		sell_per_person: sellPerPerson,
		sell_split: splitSell,
		sell_lines: sellLines,

		// Just the sales-agent uid - the page fetches their name + picture live
		// from users/{agent_id} (publicly readable), so it's never stale.
		agent_id: quote?.agent || "",

		// Optional personal note from the agent, shown above the ticket. Trimmed;
		// "" renders nothing (the page hides the whole block). Client-safe text.
		agent_message: (quote?.agent_message || "").trim(),

		// Quote expiry (epoch millis), stamped at send time from the chosen
		// validity. Past it the page hides all book / secure buttons (the page
		// itself stays viewable). Absent = never expires, so a re-publish that
		// doesn't carry it (older quote, copy-link) leaves any existing value be.
		expires_at: quote?.expires_at ? Number(quote.expires_at) : undefined,

		// Quote-only: hide every book / secure button on the public page and point
		// the client at their sales agent instead (the deposit endpoint also
		// refuses these). Defaults to false.
		book_disabled: Boolean(quote?.book_disabled),

		// Extra amount (GBP) added to the deposit for flights the agent flagged to
		// charge up front, so we can ticket them immediately. Just the total they
		// pay - no per-flight cost is exposed. Recomputed authoritatively server-side.
		flight_deposit: computeFlightDeposit(basket, quote?.exchange_rates || {}, groupSize),

		// Full-balance threshold (weeks before travel) chosen at send time. Drives
		// the deposit-vs-full-balance display + balance-due date on the public page.
		// Absent leaves any existing value be; the page defaults to 12 weeks.
		full_balance_weeks: Number(quote?.full_balance_weeks) > 0 ? Number(quote.full_balance_weeks) : undefined,
	})
}

/**
 * Publish (or refresh) the public page for a quote. Resolves/creates the quote's
 * stable `public_token`, fetches the sales agent's public details, writes the
 * sanitised `public_quotes/{token}` doc, and stamps the token back onto the
 * quote so re-sending updates the same public page.
 *
 * Returns { token, url } - the absolute public URL is built from the current
 * origin (the staff app and the public route are served from the same host).
 */
export const publishPublicQuote = async (enquiryID, quote) => {
	if (!enquiryID || !quote?.id) throw new Error("publishPublicQuote: enquiryID and quote.id are required")

	const token = quote.public_token || db.collection("public_quotes").doc().id

	// Resolve the owning client off the enquiry so it can be stamped onto the
	// public doc. The public projection outlives the enquiry (which is deleted on
	// conversion), so this is what keeps the quote - and its live /quote/{token}
	// link - on the client's profile after they book.
	const clientID = (await db.doc(`enquiries/${enquiryID}`).get()).data()?.client || ""

	const payload = buildPublicQuoteDoc({ quote, token, enquiryID, quoteID: quote.id, clientID })

	// First publish stamps `created` + `status: "active"`; later re-publishes only
	// refresh the content + `updated`, leaving status (booked/cancelled) intact.
	const isFirstPublish = !quote.public_token

	await db.doc(`public_quotes/${token}`).set(
		{
			...payload,
			updated: firebase.firestore.FieldValue.serverTimestamp(),
			...(isFirstPublish ? { created: firebase.firestore.FieldValue.serverTimestamp(), status: "active" } : {}),
		},
		{ merge: true },
	)

	// Keep the real quote doc in step with what we just published. The server
	// charges off `full_balance_weeks` on the QUOTE doc, while the public page
	// reads it from the public doc - so the quote doc must never lag the page, or
	// a client could be shown one deposit/full-balance split and charged another.
	// Stamp the token on first publish (as before) and the threshold on every
	// publish, guarded so an absent value never wipes a stored one.
	const quoteUpdate = isFirstPublish ? { public_token: token } : {}
	if (payload.full_balance_weeks !== undefined) quoteUpdate.full_balance_weeks = payload.full_balance_weeks
	if (Object.keys(quoteUpdate).length > 0) {
		await db.doc(`enquiries/${enquiryID}/quotes/${quote.id}`).set(quoteUpdate, { merge: true })
	}

	return { token, url: `${quoteOrigin()}/quote/${token}` }
}

/**
 * Update a published quote's lifecycle status (e.g. "cancelled" when an agent
 * cancels the quote). No-op when the quote was never published.
 */
export const setPublicQuoteStatus = async (token, status) => {
	if (!token) return
	await db.doc(`public_quotes/${token}`).set(
		{ status, updated: firebase.firestore.FieldValue.serverTimestamp() },
		{ merge: true },
	)
}

/** Build the public URL for an already-published token (no write). */
export const publicQuoteUrl = (token) => {
	return token ? `${quoteOrigin()}/quote/${token}` : ""
}
